import { useContext, useEffect, useState } from 'react';
import { NewProcessFields } from '../../../../api/process/admin';
import Input from '../../../../components/input/Input';
import TagInput from '../../../../components/input/TagInput';
import UserSelect from '../../../../components/input/UserSelect';
import './ProcessDetailsForm.css';
import { AppContext } from '../../../../App';
import Select from '../../../../components/input/Select';
import UserChip from '../../../../components/user-chip/UserChip';

const emptyProcess: NewProcessFields = {
  name: '',
  description: '',
  itemName: '',
  itemDisplayField: '',
  itemFields: [],
  maxItems: 1,
  ownerGroups: [],
  supportingData: [],
  category: '',
  taxonomyLabel: '',
  ownerId: '',
  allowMoveBackSteps: false,
  allowCaseDueDateUpdate: false,
};

interface Props {
  onSubmit: (formData: NewProcessFields) => void | Promise<void>;
  initialData?: Partial<NewProcessFields>;
  readOnly?: boolean;
}

const ProcessDetailsForm: React.FC<Props> = ({ onSubmit, initialData = {}, readOnly }) => {
  const ctx = useContext(AppContext);
  const [formData, setFormData] = useState<NewProcessFields>({ ...emptyProcess, ...initialData });

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(formData);
  };

  useEffect(() => {
    if (initialData.owner) {
      setFormData({ ...emptyProcess, ...initialData });
    }
  }, [initialData]);

  const isFormValid = () => {
    return formData.name && !!formData.ownerGroups?.length;
  };

  const processCategoryOptions = ctx.user.company.processCategories.map((processCategory) => {
    return {
      value: processCategory,
      label: processCategory,
    }
  })

  return (
    <form onSubmit={handleFormSubmit}>
      <Input
        id="taxonomyLabel"
        labelText="Taxonomy Label (optional)"
        value={formData.taxonomyLabel || ''}
        onChange={(taxonomyLabel) => setFormData({ ...formData, taxonomyLabel })}
        readOnly={readOnly}
      />
      <Input
        id="name"
        labelText="Name*"
        value={formData.name || ''}
        onChange={(name) => setFormData({ ...formData, name })}
        readOnly={readOnly}
        inputClassName="process-details-form__name-input"
      />
      <Input
        id="description"
        labelText="Description (optional)"
        value={formData.description || ''}
        onChange={(description) => setFormData({ ...formData, description })}
        readOnly={readOnly}
      />
      <Select
        id='category'
        options={processCategoryOptions}
        labelText='Category (optional)'
        value={formData.category}
        onChange={(category) => setFormData({ ...formData, category })}
        readOnly={readOnly}
      />
      {!readOnly && (
        <UserSelect
          id="ownerId"
          labelText="Process Owner (optional)"
          onSelect={(ownerId) => setFormData({ ...formData, ownerId })}
          userIdsToExclude={[]}
        />
      )}
      {!!readOnly && (
        <p>Process Owner</p>
      )}
      {!!formData.owner && (
        <UserChip user={formData.owner} />
      )}
      <TagInput
        id="ownerGroups"
        labelText="User Roles"
        value={formData.ownerGroups as unknown as string}
        onChange={(ownerGroups) =>
          setFormData({ ...formData, ownerGroups: ownerGroups as unknown as string[] })
        }
        small={true}
        helpText="The user roles that will be able to see, manage and complete steps in this process."
        readOnly={readOnly}
        emptyMessage='Please specify at least 1 user role'
      />
      <Input
        id="allowMoveBackSteps"
        labelText="Allow moving cases back to previous steps"
        type="checkbox"
        value={formData.allowMoveBackSteps ? 'true' : 'false'}
        onChange={(value) => setFormData({ ...formData, allowMoveBackSteps: value === 'true' })}
        readOnly={readOnly}
      />
      <Input
        id="allowCaseDueDateUpdate"
        labelText="Allow updating case due dates"
        type="checkbox"
        value={formData.allowCaseDueDateUpdate ? 'true' : 'false'}
        onChange={(value) => setFormData({ ...formData, allowCaseDueDateUpdate: value === 'true' })}
        readOnly={readOnly}
      />
      {!readOnly && (
        <button className="button" disabled={!isFormValid()}>
          {initialData ? 'Save' : 'Create Process'}
        </button>
      )}
    </form>
  );
};

export default ProcessDetailsForm;
